import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';

const TermsOfServicePage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-[#0D2340] text-white p-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white hover:bg-white/10">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <FileText className="h-6 w-6" />
          <h1 className="text-lg font-semibold">Terms of Service</h1>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
          <div>
            <p className="text-sm text-slate-500 mb-4">Last updated: February 2026</p>
            <h2 className="text-2xl font-bold text-[#0D2340] mb-4">EmergencyCard Health Terms of Service</h2>
            <p className="text-slate-600">
              Welcome to EmergencyCard Health. By using our app, you agree to these Terms of Service. Please read them carefully.
            </p>
          </div>

          <section>
            <h3 className="text-lg font-semibold text-[#0D2340] mb-2">1. Acceptance of Terms</h3>
            <p className="text-slate-600">
              By accessing or using EmergencyCard Health, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use the app.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#0D2340] mb-2">2. Description of Service</h3>
            <p className="text-slate-600">
              EmergencyCard Health is a personal health record management application that allows you to:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-slate-600 mt-2">
              <li>Store and manage medical history, prescriptions, and lab reports</li>
              <li>Track daily health metrics in a health diary</li>
              <li>Use an AI assistant (Aayu) for health record management</li>
              <li>Share records with healthcare providers via secure QR codes</li>
              <li>Export health data as PDF documents</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#0D2340] mb-2">3. Not Medical Advice</h3>
            <p className="text-slate-600 font-medium text-red-600 bg-red-50 p-3 rounded">
              IMPORTANT: EmergencyCard Health is a health record storage tool, NOT a medical service. The app does not provide medical advice, diagnosis, or treatment. Always consult qualified healthcare professionals for medical decisions.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#0D2340] mb-2">4. User Responsibilities</h3>
            <ul className="list-disc pl-5 space-y-2 text-slate-600">
              <li>Provide accurate information when creating your account</li>
              <li>Keep your login credentials secure</li>
              <li>Ensure health data you enter is accurate</li>
              <li>Use the app only for lawful purposes</li>
              <li>Not share access with unauthorized persons</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#0D2340] mb-2">5. Account Security</h3>
            <p className="text-slate-600">
              You are responsible for maintaining the security of your account. Notify us immediately if you suspect unauthorized access. We are not liable for losses due to compromised credentials.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#0D2340] mb-2">6. Data Accuracy</h3>
            <p className="text-slate-600">
              You are solely responsible for the accuracy of health information you enter. EmergencyCard Health stores data as provided and does not verify medical accuracy.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#0D2340] mb-2">7. Doctor Access Feature</h3>
            <p className="text-slate-600">
              When you generate a QR code for doctor access:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-slate-600 mt-2">
              <li>You authorize temporary read-only access to your records</li>
              <li>Access automatically expires after the time you set</li>
              <li>You can revoke access at any time</li>
              <li>We log all access for your security</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#0D2340] mb-2">8. AI Assistant (Aayu)</h3>
            <p className="text-slate-600">
              The Aayu AI assistant helps manage your health records through natural language. Aayu does not provide medical advice. Any health-related responses are for informational purposes only.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#0D2340] mb-2">9. Limitation of Liability</h3>
            <p className="text-slate-600">
              EmergencyCard Health is provided "as is" without warranties of any kind. We are not liable for:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-slate-600 mt-2">
              <li>Medical decisions made based on stored data</li>
              <li>Data loss due to technical issues (though we take precautions)</li>
              <li>Unauthorized access due to user negligence</li>
              <li>Service interruptions or downtime</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#0D2340] mb-2">10. Intellectual Property</h3>
            <p className="text-slate-600">
              EmergencyCard Health, including its logo, design, and features, is owned by us. You may not copy, modify, or distribute any part of the app without permission.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#0D2340] mb-2">11. Termination</h3>
            <p className="text-slate-600">
              We may suspend or terminate your account if you violate these Terms. You may delete your account at any time through the Settings page.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#0D2340] mb-2">12. Changes to Terms</h3>
            <p className="text-slate-600">
              We may modify these Terms at any time. Continued use of the app after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#0D2340] mb-2">13. Governing Law</h3>
            <p className="text-slate-600">
              These Terms are governed by the laws of India. Any disputes shall be resolved in the courts of [Your City], India.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#0D2340] mb-2">14. Contact</h3>
            <p className="text-slate-600">
              For questions about these Terms, contact us at: legal@sehatsetu.com
            </p>
          </section>

          <div className="pt-4 border-t">
            <p className="text-sm text-slate-500 text-center">
              EmergencyCard Health - Your Digital Health Passport<br />
              Made with care in India
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
